/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class Cart extends HTMLElement {
  constructor () {
    super();

    this.bindEvents();
    this.showOffcanvasIfUrl();
    this.adjustCollapses();
  }

  bindEvents () {
    // qty inputs
    this.querySelectorAll('[name="updates[]"]').forEach(input => {
      input.addEventListener('change', () => {
        const qty = Math.max(1, Number(input.value) || 1);
        input.value = qty;
        this.change(input.dataset.lineItemKey, qty);
      });
    });

    // qty minus/plus buttons (no negative qty ever)
    this.querySelectorAll('[data-qty-minus]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.closest('[data-qty-wrap]')?.querySelector('[name="updates[]"]');
        if (!input) return;
        const current = Number(input.value) || 1;
        const next = Math.max(1, current - 1);
        input.value = next;
        input.dispatchEvent(new Event('change'));
      });
    });

    this.querySelectorAll('[data-qty-plus]').forEach(btn => {
      btn.addEventListener('click', () => {
        const input = btn.closest('[data-qty-wrap]')?.querySelector('[name="updates[]"]');
        if (!input) return;
        const current = Number(input.value) || 1;
        const next = current + 1;
        input.value = next;
        input.dispatchEvent(new Event('change'));
      });
    });

    // remove
    this.querySelectorAll('.btn-line-item-remove').forEach(btn => {
  btn.addEventListener('click', () => this.removeLineWithAddons(btn.dataset.lineItemKey));
});
  }

  async removeLineWithAddons(parentKey) {
  this.classList.add('loading');

  // get current cart so we can find linked addons
  const cartRes = await fetch(`${window.Shopify.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } });
  const cart = await cartRes.json();

  const updates = {};
  updates[parentKey] = 0;

  cart.items.forEach(item => {
    if (item.properties && item.properties._lux_is_addon == '1' && item.properties._lux_parent_key === parentKey) {
      updates[item.key] = 0;
    }
  });

  const response = await fetch(`${window.Shopify.routes.cart_update_url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({
      updates,
      sections: ['cart-count-badge', 'offcanvas-cart'],
      sections_url: window.location.pathname
    })
  });

  const data = await response.json();

  if (response.ok && data.sections) {
    this.reloadCartElements(data.sections);
  }

  this.classList.remove('loading');
}

  async refresh () {
    const response = await fetch(
      `${window.Shopify.routes.cart_url}.js?sections=cart-count-badge,offcanvas-cart&sections_url=${encodeURIComponent(window.location.pathname)}`,
      { headers: { 'Accept': 'application/json' } }
    );

    const data = await response.json();

    if (response.ok && data?.sections) {
      this.reloadCartElements(data.sections);
      window.dispatchEvent(new CustomEvent('kt.cart.reloaded'));
    }
  }

  async add (formData) {
  // always refresh drawer sections
  formData.append('sections', ['cart-count-badge', 'offcanvas-cart']);
  formData.append('sections_url', window.location.pathname);

  const response = await fetch(`${window.Shopify.routes.cart_add_url}.js`, {
    method: 'POST',
    headers: { 'Accept': 'application/json' },
    body: formData
  });

  const data = await response.json();

  if (response.ok) {
    window.dispatchEvent(new CustomEvent('kt.cart.added', { detail: data }));
    if (data.sections) this.reloadCartElements(data.sections);
  } else {
    this.showError(data.description || 'Could not add to cart');
  }

  // IMPORTANT: do NOT auto-open the cart drawer here anymore.
  return data;
}

  async change (id, quantity) {
    this.classList.add('loading');

    // If changing parent qty, sync its addons qty too
if (quantity >= 1) {
  const cartRes = await fetch(`${window.Shopify.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } });
  const cart = await cartRes.json();

  const parentKey = id;
  const updates = {};

  cart.items.forEach(item => {
    if (item.properties && item.properties._lux_is_addon == '1' && item.properties._lux_parent_key === parentKey) {
      updates[item.key] = quantity;
    }
  });

  if (Object.keys(updates).length) {
    await fetch(`${window.Shopify.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates })
    });
  }
}

    const response = await fetch(`${window.Shopify.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id,
        quantity,
        sections: ['cart-count-badge', 'offcanvas-cart'],
        sections_url: window.location.pathname
      })
    });

    const data = await response.json();

    if (response.ok) {
      window.dispatchEvent(new CustomEvent('kt.cart.changed', { detail: data }));
      if (data.sections) this.reloadCartElements(data.sections);
    } else {
      this.showError(data.description || 'Could not update cart');
    }

    this.classList.remove('loading');
  }

  reloadCartElements (sections) {
    for (const [key, value] of Object.entries(sections)) {
      const newDoc = new DOMParser().parseFromString(value, 'text/html');

      switch (key) {
        case 'cart-count-badge':
          document.querySelectorAll('.cart-count-badge').forEach(elem => {
            const doc = new DOMParser().parseFromString(value, 'text/html');
            const next = doc.querySelector('.cart-count-badge');
            if (next) elem.replaceWith(next);
          });
          break;

        case 'offcanvas-cart': {
          const nextCart = newDoc.querySelector('cart-container');
          if (nextCart) this.replaceWith(nextCart);
          break;
        }
      }
    }

    window.dispatchEvent(new CustomEvent('kt.cart.reloaded'));
  }

  showError (message) {
    const alert = this.querySelector('#cart-alert');
    if (!alert) return;
    alert.querySelector('[data-alert-msg]').innerHTML = message;
    alert.removeAttribute('hidden');
  }

  showOffcanvasIfUrl () {
    if (new URLSearchParams(window.location.search).has('cart')) {
      const offcanvas = document.querySelector('#offcanvas-cart');
      if (offcanvas) window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).show();
    }
  }

  adjustCollapses () {
    this.querySelectorAll('[data-bs-toggle="collapse"]').forEach(elem => {
      elem.addEventListener('click', () => {
        setTimeout(() => {
          const offcanvasBody = this.querySelector('.offcanvas-body');
          if (offcanvasBody) offcanvasBody.scroll({ top: offcanvasBody.scrollHeight, behavior: 'smooth' });
        }, 250);
      });
    });
  }
}

customElements.define('cart-container', Cart);

class CartNote extends HTMLElement {
  constructor () {
    super();
    this.input = this.querySelector('textarea');
    this.btn = this.querySelector('button');
    if (this.btn) this.btn.addEventListener('click', this.onSubmit.bind(this));
  }

  async onSubmit () {
    if (!this.btn) return;
    this.btn.classList.add('loading');

    const res = await fetch(`${window.Shopify.routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ note: this.input?.value || '' })
    });

    if (res.ok) {
      this.btn.innerHTML = `✓ <span class="visually-hidden">${this.btn.dataset.textNoteSaved || 'Saved'}</span>`;
      setTimeout(() => {
        this.btn.innerHTML = this.btn.dataset.textBtnSave || 'Save note';
      }, 2000);
    }

    this.btn.classList.remove('loading');
  }
}

customElements.define('cart-note', CartNote);